import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Trash2, Users } from 'lucide-react';
import { customerService } from '../api/services/customerService';
import {
  Button,
  Badge,
  Card,
  PageHeader,
} from '@license-management/ui';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import ConfirmDialog from '../components/common/ConfirmDialog';

export const CustomerTrash: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedCustomer, setSelectedCustomer] = React.useState<string | null>(null);
  
  // Add state for permanent delete confirmation
  const [permanentDeleteConfirm, setPermanentDeleteConfirm] = React.useState<{
    isOpen: boolean;
    customerId: string | null;
    name: string;
  }>({
    isOpen: false,
    customerId: null,
    name: ''
  });

  const { data: customers, isLoading, error } = useQuery({
    queryKey: ['deletedCustomers'],
    queryFn: () => customerService.getDeletedCustomers(),
  });

  const restoreMutation = useMutation({
    mutationFn: (customerId: string) => customerService.restoreCustomer(customerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['deletedCustomers'] });
      setSelectedCustomer(null);
    },
  });
  
  // Add mutation for checking if customer can be permanently deleted
  const checkCustomerDeletableMutation = useMutation({
    mutationFn: (customerId: string) => customerService.checkCustomerDeletable(customerId),
  });
  
  // Add mutation for permanently deleting a customer
  const permanentDeleteMutation = useMutation({
    mutationFn: (customerId: string) => customerService.hardDeleteCustomer(customerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['deletedCustomers'] });
      setPermanentDeleteConfirm({
        isOpen: false,
        customerId: null,
        name: ''
      });
    },
  });
  
  // Handle checking if a customer can be deleted and showing confirmation
  const handlePermanentDelete = async (customerId: string, customerName: string) => {
    try {
      const result = await checkCustomerDeletableMutation.mutateAsync(customerId);
      
      if (result.canDelete) {
        // Customer can be deleted, show confirmation dialog
        setPermanentDeleteConfirm({
          isOpen: true,
          customerId,
          name: customerName
        });
      } else {
        // Show why the customer can't be deleted
        alert(result.message || 'This customer cannot be deleted at this time.');
      }
    } catch (error) {
      console.error('Error checking if customer can be deleted:', error);
      alert('Failed to check if customer can be deleted');
    }
  };

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message="Failed to load deleted customers" />;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Customer Recycle Bin"
        description="Deleted customers are kept for 30 days before permanent deletion"
        actions={
          <Button
            variant="secondary"
            onClick={() => navigate('/customers')}
            leftIcon={<ArrowLeft className="h-4 w-4" />}
          >
            Back to Customers
          </Button>
        }
      />

      {!customers || customers.length === 0 ? (
        <Card className="text-center py-12">
          <Users className="mx-auto h-12 w-12 text-zinc-400 mb-4" />
          <h3 className="text-lg font-medium text-zinc-900 mb-2">No deleted customers</h3>
          <p className="text-zinc-500">The recycle bin is empty.</p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-zinc-200">
              <thead className="bg-zinc-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-zinc-200">
                {customers.map((customer) => (
                  <tr key={customer.customerId} className="hover:bg-zinc-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-zinc-900">{customer.name}</div>
                      <div className="text-sm text-zinc-500">{customer.country}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-zinc-900">{customer.email}</div>
                      {customer.phone && (
                        <div className="text-sm text-zinc-500">{customer.phone}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant="error">Deleted</Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setSelectedCustomer(customer.customerId)}
                          leftIcon={<RefreshCw className="h-4 w-4" />}
                        >
                          Restore
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handlePermanentDelete(customer.customerId, customer.name)}
                          leftIcon={<Trash2 className="h-4 w-4" />}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <ConfirmDialog
        isOpen={!!selectedCustomer}
        title="Reactivate Customer"
        message="Are you sure you want to reactivate this customer? They will be visible in the customer list again."
        confirmLabel="Reactivate"
        onConfirm={() => {
          if (selectedCustomer) {
            restoreMutation.mutate(selectedCustomer);
          }
        }}
        onCancel={() => setSelectedCustomer(null)}
      />
      
      {/* Add confirmation dialog for permanent deletion */}
      <ConfirmDialog
        isOpen={permanentDeleteConfirm.isOpen}
        title="Permanently Delete Customer"
        message={`Are you sure you want to permanently delete ${permanentDeleteConfirm.name}? This action CANNOT be undone, and all associated licenses and activations will be deleted.`}
        confirmLabel="Yes, Delete Permanently"
        cancelLabel="Cancel"
        severity="high"
        onConfirm={() => {
          if (permanentDeleteConfirm.customerId) {
            permanentDeleteMutation.mutate(permanentDeleteConfirm.customerId);
          }
        }}
        onCancel={() => setPermanentDeleteConfirm({
          isOpen: false,
          customerId: null,
          name: ''
        })}
      />
    </div>
  );
};
